import { useCallback, useRef } from 'react';
import { type MutationFunctionOptions, type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';

import { useMemoObj } from '../useMemoObj.ts';

import {
	getMutationResultError,
	type MutationResultLike,
	syncMutationCacheBridgeSafely,
	type MutationCacheBridgeSource,
} from './mutationCacheBridgeSync.ts';
import {
	syncMutationCacheBridgeAfterOptimisticRollback,
	syncOptimisticMutationCacheBridge,
} from './mutation/optimisticMutationPayloadSync.ts';

type MutationOptionsLike<TData, TVariables> = Pick<
	MutationFunctionOptions<TData, TVariables>,
	'variables' | 'optimisticResponse' | 'update'
>;

type MutationExecutor<
	TData,
	TVariables extends Record<string, unknown>,
	TResult extends MutationResultLike<TData>,
> = (options?: MutationOptionsLike<TData, TVariables>) => Promise<TResult>;

export function useMutationCacheBridgeSync<
	TData,
	TVariables extends Record<string, unknown> = OperationVariables,
	TResult extends MutationResultLike<TData> = { data?: TData },
>({
	source,
	mutation,
	mutate,
	defaultVariables,
	defaultOptimisticResponse,
	fetchPolicy,
	useAggClient,
	callerPackage,
}: {
	source: MutationCacheBridgeSource;
	mutation: GraphQLTaggedNode;
	mutate: MutationExecutor<TData, TVariables, TResult>;
	defaultVariables?: TVariables;
	defaultOptimisticResponse?: TData | null;
	fetchPolicy?: string;
	useAggClient?: boolean;
	callerPackage?: string;
}): MutationExecutor<TData, TVariables, TResult> {
	const stableDefaultVariables = useMemoObj<TVariables>(defaultVariables, callerPackage);
	const stableDefaultVariablesRef = useRef(stableDefaultVariables);
	stableDefaultVariablesRef.current = stableDefaultVariables;
	const defaultOptimisticResponseRef = useRef(defaultOptimisticResponse);
	defaultOptimisticResponseRef.current = defaultOptimisticResponse;

	return useCallback(
		async (options) => {
			const variables = options?.variables ?? stableDefaultVariablesRef.current;
			const optimisticResponse =
				options?.optimisticResponse ?? defaultOptimisticResponseRef.current;
			const optimisticData =
				typeof optimisticResponse === 'function' ? undefined : optimisticResponse;
			const optimisticRollback =
				optimisticData == null
					? null
					: syncOptimisticMutationCacheBridge({
							source,
							mutation,
							variables,
							data: optimisticData,
							fetchPolicy,
							useAggClient,
						});

			let result: TResult;
			try {
				result = await mutate(options);
			} catch (error) {
				optimisticRollback?.rollback();
				throw error;
			}

			// The original mutate promise has already resolved at this point. Cache
			// sync is secondary, so it is skipped for result-level errors and its
			// own write failures are swallowed/reported by syncMutationCacheBridge.
			syncMutationCacheBridgeAfterOptimisticRollback(optimisticRollback, () =>
				syncMutationCacheBridgeSafely({
					source,
					mutation,
					variables,
					data: result?.data,
					fetchPolicy,
					error: getMutationResultError(result),
					useAggClient,
				}),
			);

			return result;
		},
		[fetchPolicy, mutate, mutation, source, useAggClient],
	);
}
